import { useEffect, useState } from "react";
import { Search, Download, Loader2, X, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { Btn, Field, inputCls, RoleBadge } from "../components";

const STATUSES = ["New", "Reviewed", "Shortlisted", "Interview", "Selected", "Rejected", "On Hold"];

const statusCls = {
  New: "bg-blue-100 text-blue-800 border-blue-200",
  Reviewed: "bg-slate-200 text-slate-700 border-slate-300",
  Shortlisted: "bg-amber-100 text-amber-800 border-amber-200",
  Interview: "bg-purple-100 text-purple-800 border-purple-200",
  Selected: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
  "On Hold": "bg-slate-100 text-slate-500 border-slate-200",
};

const DetailRow = ({ label, value }) =>
  value ? (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800 break-words">{value}</p>
    </div>
  ) : null;

const CareerApplications = () => {
  const [items, setItems] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (jobId) params.set("job_id", jobId);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    adminApi.get(`/applications?${params}`).then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { load(); }, [jobId, status]);
  useEffect(() => { adminApi.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);

  const openDetail = async (a) => {
    try {
      const r = await adminApi.get(`/applications/${a.id}`);
      setDetail(r.data);
      setNote("");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const changeStatus = async (s) => {
    try {
      const r = await adminApi.patch(`/applications/${detail.id}`, { status: s });
      setDetail(r.data);
      toast.success(`Status updated to ${s}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      const r = await adminApi.patch(`/applications/${detail.id}`, { note });
      setDetail(r.data);
      setNote("");
      toast.success("Note added");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const downloadResume = async () => {
    try {
      const r = await adminApi.get(`/applications/${detail.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = detail.resume_filename || "resume";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download resume"); }
  };

  return (
    <div data-testid="admin-applications-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Career Applications</h1>
          <p className="mt-1 text-sm text-slate-500">Candidate applications with resumes — visible only to HR and Super Admin.</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} pl-9 w-64`} placeholder="Search name / email / phone" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="applications-search" />
        </div>
        <select className={`${inputCls} !w-56`} value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="applications-job-filter">
          <option value="">All positions</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.data?.title}</option>)}
        </select>
        <select className={`${inputCls} !w-44`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="applications-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <Btn variant="ghost" onClick={load} data-testid="applications-search-btn">Search</Btn>
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Candidate", "Position", "Contact", "Experience", "Applied", "Status"].map((h) => (
                <th key={h} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No applications found.</td></tr>
            ) : (
              items.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(a)} data-testid={`application-row-${a.id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{a.full_name}</td>
                  <td className="px-5 py-3 text-slate-600">{a.job_title}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{a.email}<br />{a.phone}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{a.total_experience || "—"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="px-5 py-3"><span className={`inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border rounded-sm ${statusCls[a.status] || statusCls.New}`}>{a.status}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-[75] flex items-start justify-center p-4 overflow-y-auto" data-testid="application-detail-modal">
          <div className="absolute inset-0 bg-[#0A1128]/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-3xl my-8">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">{detail.full_name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{detail.job_title} · Applied {fmtDate(detail.created_at)}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="application-detail-close"><X size={18} /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-3 gap-x-6 gap-y-4">
              <DetailRow label="Email" value={detail.email} />
              <DetailRow label="Phone" value={detail.phone} />
              <DetailRow label="City" value={detail.city} />
              <DetailRow label="Current Company" value={detail.current_company} />
              <DetailRow label="Current Designation" value={detail.current_designation} />
              <DetailRow label="Total Experience" value={detail.total_experience} />
              <DetailRow label="Relevant Experience" value={detail.relevant_experience} />
              <DetailRow label="Current CTC" value={detail.current_ctc} />
              <DetailRow label="Expected CTC" value={detail.expected_ctc} />
              <DetailRow label="Notice Period" value={detail.notice_period} />
              <DetailRow label="LinkedIn" value={detail.linkedin} />
              <DetailRow label="Resume" value={detail.resume_filename} />
            </div>
            {detail.cover_note && (
              <div className="px-6 pb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cover Note</p>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{detail.cover_note}</p>
              </div>
            )}
            <div className="px-6 pb-4 flex flex-wrap items-center gap-3">
              <Btn onClick={downloadResume} data-testid="download-resume-btn"><Download size={14} /> Download Resume</Btn>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</span>
                <select className={`${inputCls} !w-44 !py-2`} value={detail.status} onChange={(e) => changeStatus(e.target.value)} data-testid="application-status-select">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="px-6 pb-6 border-t border-slate-100 pt-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><StickyNote size={12} /> Internal HR Notes</p>
              <ul className="mt-3 space-y-2">
                {(detail.notes || []).map((n, i) => (
                  <li key={i} className="text-sm text-slate-700 bg-slate-50 border border-slate-200 px-3.5 py-2.5 rounded-sm">
                    {n.text}
                    <span className="block mt-1 text-[10px] text-slate-400">{n.by} · {fmtDate(n.at)}</span>
                  </li>
                ))}
                {!(detail.notes || []).length && <li className="text-xs text-slate-400">No notes yet.</li>}
              </ul>
              <form onSubmit={addNote} className="mt-3 flex gap-2">
                <input className={inputCls} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="application-note-input" />
                <Btn type="submit" variant="dark" disabled={busy} data-testid="application-note-btn">{busy ? <Loader2 size={13} className="animate-spin" /> : "Add"}</Btn>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerApplications;
